import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import AccountsSearch from "./AccountsSearch";
import AccountsSalesTable from "./AccountsSalesTable";
import PassedAccountStartDateTable from "./PassedAccountStartDateTable";
import { financialsActions } from "../../../../reducers/financials";
import NumberOfNewPurchasesByMonths from "./NumberOfNewPurchasesByMonths";
import { getQueryStringByStringValues } from "../../../ComplianceSearch/utils";
import { BILLING_API_URLS, BillingAPI } from "../../../../api/billing";
import AmountTradingDays from "./AmountTradingDays";
import PurchasesToTookEvaluation from "./PurchasesToTookEvaluation";
import { usePassersContext } from "../../../../context";

const Passers = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { passersTab, setPassersTab } = usePassersContext();

  const [reportTitleDate, setReportTitleDate] = useState("");
  const [sortBy, setSortBy] = useState({ by: "id", order: false });

  const [periodFilter, setPeriodFilter] = useState({
    startDate: "",
    endDate: "",
  });

  const { isError, error } = useQuery({
    enabled:
      (!!passersTab.search.emails && !!reportTitleDate) ||
      (!!periodFilter.startDate && !!periodFilter.endDate),
    queryKey: [
      BILLING_API_URLS.passersStats,
      sortBy,
      passersTab.search.emails,
      reportTitleDate,
      periodFilter.startDate,
      periodFilter.endDate,
    ],
    queryFn: async ({ signal }) => {
      const query = new URLSearchParams();
      query.set("sort_by", sortBy.by);
      query.set("order", sortBy.order ? "ASC" : "DESC");
      query.set("report_title_date", reportTitleDate);

      if (periodFilter.startDate) {
        query.set("start_date", periodFilter.startDate);
      }
      if (periodFilter.endDate) {
        query.set("end_date", periodFilter.endDate);
      }

      const emailsQuery = getQueryStringByStringValues("emails", passersTab.search.emails);
      const requestQuery = query.toString() + `&${emailsQuery}`;

      dispatch(financialsActions.fetchPassersStatsRequest());
      const response = await BillingAPI.getPassersStatsRequest(requestQuery, signal);
      dispatch(financialsActions.fetchPassersStatsSuccess(response.data));

      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      dispatch(financialsActions.fetchPassersStatsFailed());
    }
  }, [isError, error]);

  const handleDateChange = (startDate: string, endDate: string) => {
    dispatch(financialsActions.clearPassersStatsState());
    dispatch(financialsActions.clearPassedSubscriptionPurchaseDateState());
    dispatch(financialsActions.clearSpentToPassEvaluationState());

    setPeriodFilter({ startDate, endDate });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <AccountsSearch
          setEmails={(emails) =>
            setPassersTab({
              ...passersTab,
              search: {
                ...passersTab.search,
                emails,
              },
            })
          }
          onDateChange={handleDateChange}
          setReportTitleDate={setReportTitleDate}
          setPeriodFilter={setPeriodFilter}
        />
      </Grid>

      <Grid item xs={12}>
        <AccountsSalesTable sortBy={sortBy} setSortBy={setSortBy} />
      </Grid>

      <Grid item xs={12}>
        <PassedAccountStartDateTable
          emails={passersTab.search.emails}
          reportTitleDate={reportTitleDate}
          periodFilter={periodFilter}
        />
      </Grid>

      <Grid item xs={6}>
        <NumberOfNewPurchasesByMonths />
      </Grid>
      <Grid item xs={6}>
        <AmountTradingDays />
      </Grid>
      <Grid item xs={12}>
        <PurchasesToTookEvaluation periodFilter={periodFilter} emails={passersTab.search.emails} />
      </Grid>
    </Grid>
  );
};

export default Passers;
